/**
 * Guía: Pagos y Transacciones.
 *
 * Contenido derivado del capítulo **19** del manual: §0 (en qué se diferencia
 * del panel financiero, y por qué lista lo no cobrado), §3 y §3bis (qué trae
 * cada fila y qué añade el detalle, que consulta la pasarela en vivo) y §4
 * (los filtros, el catálogo abierto de métodos y el rango de días).
 *
 * La distinción que gobierna la guía es la del §0: **el listado incluye todo
 * y las cifras de arriba sólo cuentan lo cobrado**. Es una pantalla sin
 * acciones de escritura, así que el riesgo no es romper nada — es leer un
 * número que describe otro conjunto.
 */

import type { TourFlow } from '../types';

const PAGOS_PATH = '/finance-payments';

export const c19Pagos: TourFlow = {
  id: 'admin.finance-payments',
  chapter: '19',
  title: 'Transacciones',
  summary:
    'El libro de auditoría: qué pasó con una transacción concreta, y por qué el listado y las cifras no cuentan lo mismo.',
  href: PAGOS_PATH,
  requiresCountry: true,
  estimatedMin: 4,
  steps: [
    {
      id: 'intro',
      title: 'Aquí se mira una transacción, no el negocio',
      body:
        'El panel financiero responde «cómo vamos». Ésta responde «qué pasó con este cobro». ' +
        'Es un libro de auditoría: se usa para buscar una orden porque alguien reclama, para ' +
        'cuadrar contra el informe de la pasarela y para exportar a contabilidad.',
      route: PAGOS_PATH,
    },
    {
      id: 'includes-unpaid',
      title: 'Lista también lo que no se cobró',
      body:
        'Órdenes rechazadas, canceladas y sin pagar salen aquí, y es media razón de existir ' +
        'de la pantalla: ver una que se quedó en «pago pendiente» es lo que explica la ' +
        'mitad de los reclamos.',
      target: 'admin.payments.table',
      placement: 'top',
      optionalTarget: true,
    },
    {
      id: 'stats-only-charged',
      title: 'Las cifras de arriba sólo cuentan lo cobrado',
      body:
        'Es la distinción que hay que tener presente: el listado incluye todo y los ' +
        'totales, no. Comparar el número de filas con el importe de arriba da distinto, y ' +
        'las dos cosas son correctas.',
      target: 'admin.payments.stats',
      placement: 'bottom',
      optionalTarget: true,
    },
    {
      id: 'filters-fail-loud',
      title: 'Un filtro que no se entiende se rechaza',
      body:
        'No se descarta en silencio: el servidor responde diciendo qué valores acepta. En ' +
        'una pantalla de dinero, un filtro que no se aplica sin avisar es el peor fallo que ' +
        'hay — la respuesta es plausible y es falsa, y nada indica que estás viendo otro ' +
        'conjunto.',
    },
    {
      id: 'date-range',
      title: 'El rango es de días, y va entero',
      body:
        '«Hasta el 31» incluye el 31 completo, resuelto en la zona horaria del país. Y un ' +
        'rango a medias —una fecha puesta y la otra no— **no se aplica**: la pantalla lo ' +
        'avisa en vez de dejarte creer que sí.',
    },
    {
      id: 'method-catalog',
      title: 'Los métodos de pago salen del catálogo, no de una lista fija',
      body:
        'Tarjeta, PSE, Nequi, efectivo, billetera… cada pasarela declara los suyos, así que ' +
        'el catálogo crece al integrar un proveedor. El selector se construye del mismo ' +
        'diccionario con el que la tabla etiqueta, así que no puede ofrecerte un método que ' +
        'luego no sepa pintar.',
    },
    {
      id: 'row-anatomy',
      title: 'La referencia es lo que cuadra con la pasarela',
      body:
        'Cada fila trae la orden y su estado, el pago con su método y los últimos cuatro de ' +
        'la tarjeta, el comercio, el cliente que pagó y la referencia del proveedor. Esa ' +
        'referencia es la que se busca en el informe de la pasarela cuando algo no cuadra.',
    },
    {
      id: 'live-detail',
      title: 'El detalle pregunta a la pasarela en vivo',
      body:
        '«Ver transacción» no se limita a lo guardado: consulta el estado actual en el ' +
        'proveedor y devuelve su respuesta cruda — el código del emisor, el paso de 3DS, el ' +
        'mensaje del banco. Es lo que permite decirle a un cliente por qué le rechazaron la ' +
        'tarjeta en vez de suponerlo.',
    },
    {
      id: 'read-only',
      title: 'Desde aquí no se mueve dinero',
      body:
        'No hay acciones de escritura: ni se reembolsa, ni se reintenta un cobro, ni se ' +
        'cambia un estado. Un reembolso se pide desde el ticket del cliente y se resuelve en ' +
        'la pantalla de Reembolsos.',
    },
    {
      id: 'export',
      title: 'Exportar lo que tienes filtrado',
      body:
        'La exportación se lleva lo que hay en pantalla con sus filtros aplicados, no todo ' +
        'el país. Antes de exportar para contabilidad, comprueba el rango y el estado que ' +
        'tienes puestos.',
    },
  ],
};
